import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Check, Clock, Users, ChevronRight } from 'lucide-react';
import { PublicLayout, ScrollReveal, SectionTitle, PriceTag, Badge } from './components';
import { SITE_CONFIG, IMAGES, SERVICES, CONDITIONS } from './config';
import { SEOHead } from './SEOHead';

// ============================================
// COMPONENTE DE MENÚ CARD
// ============================================
const MenuCard = ({ menu, serviceName }) => {
  return (
    <div className={`bg-white rounded-2xl shadow-sm border overflow-hidden transition-all duration-300 hover:shadow-lg ${
      menu.popular ? 'border-emerald-500 ring-2 ring-emerald-500/20' : 'border-gray-200'
    }`}>
      {menu.popular && (
        <div className="bg-emerald-500 text-white text-center py-2 text-sm font-semibold">
          Más solicitado
        </div>
      )}
      
      <div className="p-6 lg:p-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">{menu.name}</h3>
            <p className="text-gray-600 text-sm mt-1">{menu.description}</p>
          </div>
          <PriceTag price={menu.price} />
        </div>

        <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {menu.duration}
          </span>
          <span className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            Mín. {menu.minPersons} pers.
          </span>
          {menu.pieces && (
            <span className="text-emerald-600 font-medium">{menu.pieces}</span>
          )}
        </div>

        <div className="border-t pt-6">
          <p className="text-sm font-semibold text-gray-700 mb-3">Incluye:</p>
          <ul className="space-y-2">
            {menu.includes.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <Link
          to="/web/contacto"
          state={{ service: serviceName, menu: menu.name }}
          className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-colors"
        >
          Pedir presupuesto
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};

// ============================================
// PÁGINA DE SERVICIO GENÉRICA
// ============================================
const ServicePage = ({ serviceKey, heroImage, heroAlt }) => {
  const service = SERVICES[serviceKey];
  
  if (!service) return null;

  return (
    <PublicLayout>
      <SEOHead page={serviceKey} />
      {/* Hero */}
      <section className="relative pt-20 lg:pt-24">
        <div className="relative h-64 lg:h-80">
          <img 
            src={heroImage}
            alt={heroAlt}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/30" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white">
              <ScrollReveal>
                <nav className="flex items-center justify-center gap-2 text-sm text-white/70 mb-4">
                  <Link to="/web" className="hover:text-white">Inicio</Link>
                  <ChevronRight className="w-4 h-4" />
                  <span className="text-white">{service.title}</span>
                </nav>
                <h1 className="text-4xl lg:text-5xl font-bold">{service.title}</h1>
                <p className="mt-4 text-lg text-white/80 max-w-xl mx-auto px-4">
                  {service.shortDescription}
                </p>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Menús */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <SectionTitle 
              badge="Nuestros menús"
              title={`Elige el ${service.title.toLowerCase()} perfecto`}
              subtitle={`Desde ${service.priceFrom}€ por persona. Todos los precios sin IVA.`}
            />
          </ScrollReveal>

          {service.note && (
            <ScrollReveal>
              <div className="max-w-3xl mx-auto mb-12 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-center">
                <p className="text-emerald-800 text-sm">{service.note}</p>
              </div>
            </ScrollReveal>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {service.menus.map((menu, index) => (
              <ScrollReveal key={menu.name} delay={index * 100}>
                <MenuCard menu={menu} serviceName={service.title} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Condiciones */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <SectionTitle 
              badge="Información importante"
              title="Condiciones del servicio"
            />
          </ScrollReveal>

          <ScrollReveal delay={100}>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Mínimos y suplementos</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Mínimo {CONDITIONS.minPersons} personas</li>
                  <li>• Menos de {CONDITIONS.minPersons}: suplemento según servicio</li>
                  <li>• Todos los precios SIN IVA</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Transporte y entrega</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Gratis: {CONDITIONS.transport.free}</li>
                  <li>• Madrid Central: +{CONDITIONS.transport.madridCentral}€ o parking</li>
                  <li>• Entrega máx. 30 min antes del evento</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Alergias e intolerancias</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Menús adaptados disponibles</li>
                  <li>• Opciones vegetarianas y veganas</li>
                  <li>• Presupuesto personalizado</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-3">¿Qué incluye?</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Menaje, mantelería y decoración</li>
                  <li>• Montaje en tu espacio</li>
                  <li>• Recogida posterior</li>
                </ul>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-3xl font-bold text-white mb-4">
              ¿Necesitas un presupuesto personalizado?
            </h2>
            <p className="text-lg text-emerald-100 mb-8">
              Cuéntanos tu evento y te preparamos una propuesta a medida.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/web/contacto"
                className="w-full sm:w-auto px-8 py-4 bg-white text-emerald-700 font-semibold rounded-full hover:bg-emerald-50 transition-colors"
              >
                Solicitar presupuesto
              </Link>
              <a
                href={`tel:${SITE_CONFIG.phone.replace(/\s/g, '')}`}
                className="w-full sm:w-auto px-8 py-4 border-2 border-white/50 text-white font-medium rounded-full hover:bg-white/10 transition-colors"
              >
                Llamar: {SITE_CONFIG.phone}
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  );
};

// ============================================
// PÁGINAS ESPECÍFICAS
// ============================================
export const DesayunosPage = () => (
  <ServicePage 
    serviceKey="desayunos"
    heroImage={IMAGES.desayunos.main}
    heroAlt="Desayunos para empresas en Madrid - Coffee break corporativo"
  />
);

export const LunchPage = () => (
  <ServicePage 
    serviceKey="lunch"
    heroImage={IMAGES.lunch.main}
    heroAlt="Lunch y buffet para empresas en Madrid - Catering corporativo"
  />
);

export const CoctelesPage = () => (
  <ServicePage 
    serviceKey="cocteles"
    heroImage={IMAGES.lunch.buffet}
    heroAlt="Cócteles para empresas en Madrid - Eventos corporativos"
  />
);

export default ServicePage;
